import React from 'react';

const DatePicker: React.FC<{
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedDate, setSelectedDate] = React.useState<Date | null>(value ? new Date(value.split('/').reverse().join('-')) : null);

    const formatDate = (date: Date) => {
        return date.toLocaleDateString('pt-BR');
    };

    const parseDate = (dateStr: string) => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split('/');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };

    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    React.useEffect(() => {
        if (value) {
            const parsed = parseDate(value);
            setSelectedDate(parsed);
        } else {
            setSelectedDate(null);
        }
    }, [value]);

    const handleDateSelect = (date: Date) => {
        const formatted = formatDate(date);
        onChange(formatted);
        setSelectedDate(date);
        setIsOpen(false);
    };

    const generateCalendarDays = () => {
        const today = new Date();
        const currentMonth = selectedDate || today;
        const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay());

        const days = [];
        const current = new Date(startDate);

        for (let i = 0; i < 42; i++) {
            days.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }

        return days;
    };

    const monthNames = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];

    const navigateMonth = (direction: number) => {
        const newDate = new Date(selectedDate || new Date());
        newDate.setMonth(newDate.getMonth() + direction);
        setSelectedDate(newDate);
    };

    return (
        <div className="relative" ref={containerRef}>
            <div className="relative">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="px-3 py-2 pr-10 border border-slate-300 rounded-md text-sm w-40 cursor-pointer"
                    onClick={() => setIsOpen(!isOpen)}
                    readOnly
                />
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </button>
            </div>

            {isOpen && (
                <div className="absolute z-50 mt-1 bg-white border border-slate-300 rounded-lg shadow-lg p-4 w-64">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={() => navigateMonth(-1)}
                            className="p-1 hover:bg-slate-100 rounded"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <h3 className="font-semibold text-slate-800">
                            {monthNames[(selectedDate || new Date()).getMonth()]} {(selectedDate || new Date()).getFullYear()}
                        </h3>

                        <button
                            onClick={() => navigateMonth(1)}
                            className="p-1 hover:bg-slate-100 rounded"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                            <div key={day} className="text-center text-xs font-medium text-slate-500 py-1">
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                        {generateCalendarDays().map((day, index) => {
                            const isCurrentMonth = day.getMonth() === (selectedDate || new Date()).getMonth();
                            const isSelected = selectedDate &&
                                day.toDateString() === selectedDate.toDateString();
                            const isToday = day.toDateString() === new Date().toDateString();

                            return (
                                <button
                                    key={index}
                                    onClick={() => handleDateSelect(day)}
                                    className={`
                    w-8 h-8 text-sm rounded hover:bg-slate-100 transition-colors
                    ${!isCurrentMonth ? 'text-slate-300' : 'text-slate-700'}
                    ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                    ${isToday && !isSelected ? 'border border-blue-600 text-blue-600' : ''}
                  `}
                                >
                                    {day.getDate()}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;
